import { clsx } from 'clsx';

import { Button } from 'UI/Forms/Button/Button';

import * as styles from './FollowUser.module.scss';

import type { ButtonT } from 'UI/Forms/Button/types';

type ButtonTypesT = 'default' | 'black';

type ButtonTextTypesT = 'default' | 'subscribe';

type PropsT = {
  readonly isFollowing: boolean;
  readonly dataTestId?: string;
  readonly onClick: any;
  readonly isLoading: boolean;
  readonly rootClassName?: ButtonT['rootClassName'];
  readonly buttonType?: ButtonTypesT;
  readonly size?: ButtonT['size'];
  readonly wide?: ButtonT['wide'];
  readonly buttonTextType?: ButtonTextTypesT;
  readonly disabled?: boolean;
  readonly userName: string;
};

const buttonTypes = {
  default: {
    followButtonVariant: 'important-invert',
    unFollowButtonVariant: 'secondary-invert',
    rootClassName: '',
  },
  black: {
    followButtonVariant: 'black',
    unFollowButtonVariant: 'black',
    rootClassName: `${styles.blackButton} py-8 px-18`,
  },
} as const;

const buttonTextTypes = {
  default: {
    follow: 'Follow',
    following: 'Following',
    unfollow: 'Unfollow',
  },
  subscribe: {
    follow: 'Subscribe',
    following: 'Subscribed',
    unfollow: 'Unsubscribe',
  },
} as const;

export const FollowUser = ({
  dataTestId,
  isFollowing,
  onClick,
  isLoading,
  rootClassName,
  buttonType = 'default',
  size = 'medium',
  wide,
  buttonTextType = 'default',
  disabled,
  userName,
}: PropsT) => {
  const {
    followButtonVariant,
    unFollowButtonVariant,
    rootClassName: buttonRootClassName,
  } = buttonTypes[buttonType];
  const buttonText = buttonTextTypes[buttonTextType];
  const buttonVariant = isFollowing
    ? followButtonVariant
    : unFollowButtonVariant;

  return (
    <Button
      ariaLabel={
        isFollowing
          ? `${userName} is following, click to ${buttonText.unfollow}`
          : `${buttonText.follow} ${userName}`
      }
      data-state-text={isFollowing ? buttonText.unfollow : buttonText.following}
      dataTestId={dataTestId}
      disabled={disabled}
      isActive={isFollowing}
      pending={isLoading}
      rootClassName={[
        buttonRootClassName,
        isFollowing && styles.following,
        rootClassName,
      ]}
      size={size}
      variant={buttonVariant}
      wide={wide}
      onClick={onClick}
    >
      <span
        aria-hidden="true"
        className={clsx(
          styles.buttonTextWrapper,
          'relative flex justify-center',
        )}
        data-state-placeholder={buttonText.following}
      >
        <span className="absolute">
          {isFollowing ? buttonText.following : buttonText.follow}
        </span>
      </span>
    </Button>
  );
};
